/**
 * step-tone — wspólna logika „tonu" kroku procesu (F17 T17.1.8c).
 *
 * Wyodrębnione z `ApprovalProgressMini` (poziomy stepper na banerze/historii) — ton kroku
 * (`toneOf`) i tokeny kolorów, wspólne dla stepera i dla diagramu sprawy
 * (`ProcessGraphViewer`, przycisk „Pokaż proces" na karcie rekordu). Tu też nazwa drogi
 * wybranej w decyzji (`findRouteEdge` / `routeNameOf`) — wspólna dla stepera i historii.
 *
 * Na stepperze „bieżący" zależy WYŁĄCZNIE od statusu kroku (`pending`/`in_progress`) —
 * gdy droga prowadzi z powrotem do wcześniejszego etapu, węzeł jest odwiedzany ponownie, więc
 * stary (zakończony) krok o tym samym `nodeId` nie może wyglądać na „bieżący".
 *
 * Diagram sprawy rysuje inny wymiar: nie historię kroków, tylko GDZIE JEST sprawa — węzły
 * z `instance.currentNodeIds` (sprawa w toku) albo etap końcowy `instance.finalNodeId`
 * (sprawa zakończona). Ten podział (`CaseMarker`) i jego tokeny są tu, obok tonów kroków,
 * żeby stepper i diagram używały tej samej barwy akcentu.
 */

import { AlertTriangle, Check, Clock, Dot, type LucideIcon, Minus } from 'lucide-react';

import type { InstanceStep, SnapshotEdge, SnapshotNode } from './instance-api';

export type Tone = 'approved' | 'done' | 'current' | 'skipped' | 'error' | 'pending';

// Tylko tokeny z tailwind.config.js (accent/bg/border/text) + standardowa paleta Tailwinda
// (green/red/amber/sky/orange — jak ToastContext). NIE `success`/`error`/`base` (nie istnieją
// w configu → renderują się jako brak koloru = niewidoczne).
export const TONE_DOT: Record<Tone, string> = {
  approved: 'bg-green-600 text-white',
  // Krok zakończony wyborem drogi — neutralny: nazwę drogi nadaje autor procesu, więc nie wiemy,
  // czy to „zgoda" czy „odmowa" (jak FINAL_NODE_CLASS dla etapu końcowego).
  done: 'bg-slate-500 text-white',
  current: 'bg-accent-600 text-white ring-4 ring-accent-600/20',
  skipped: 'border border-border-secondary bg-bg-tertiary text-text-secondary',
  error: 'bg-orange-500 text-white',
  pending: 'border border-border-primary bg-bg-tertiary text-text-secondary',
};

export const TONE_ICON: Record<Tone, LucideIcon> = {
  approved: Check,
  done: Dot,
  current: Clock,
  skipped: Minus,
  error: AlertTriangle,
  pending: Clock,
};

/**
 * Czy krok zamknięto akcją „Cofnij do poprzedniego etapu" (decyzja `step_back`). Cofnięcie zamyka
 * krok od razu — także wtedy, gdy część głosujących zdążyła już oddać głos za jakąś drogą — więc
 * sama obecność takiej decyzji znaczy, że to ona ten krok zamknęła.
 */
export function wasSteppedBack(step: InstanceStep): boolean {
  return step.decisions.some((d) => d.decisionType === 'step_back');
}

/** Wyznacza ton kroku — patrz uwaga o „bieżącym" w nagłówku modułu (droga z powrotem do etapu). */
export function toneOf(step: InstanceStep): Tone {
  if (step.status === 'pending' || step.status === 'in_progress') return 'current';
  if (step.status === 'skipped') return 'skipped';
  if (step.status === 'error' || step.status === 'timed_out') return 'error';
  if (step.status === 'done') {
    // Cofnięcie zamyka krok bez drogi (`routeEdgeId` = null) i bez głosu `approve`, więc bez tego
    // przypadku wpadłoby w „approved" (zielone ✓) — jakby etap został zatwierdzony. To akcja
    // neutralna jak droga: ton „done" (napis „Cofnięto" dokłada stepper).
    if (wasSteppedBack(step)) return 'done';
    const types = step.decisions.map((d) => d.decisionType);
    // Model dróg: każda decyzja człowieka to technicznie `approve` + wybrana droga
    // (`routeEdgeId`), a droga bywa „Odrzuć". Krok zakończony drogą nie jest więc „zatwierdzony"
    // — dostaje ton neutralny. Także `approve` bez drogi: zostaje tylko w historii starych spraw
    // (dawna ścieżka błędu „etap bez dostępnej drogi"; dziś serwer odrzuca taką decyzję przed
    // zapisem), więc zielone ✓ wprowadzałoby w błąd.
    if (step.decisions.some((d) => d.routeEdgeId !== null)) return 'done';
    if (types.includes('approve')) return 'done';
    // Każdy inny zakończony krok (bez głosu `approve` i bez wybranej drogi — np. scalenie gałęzi
    // równoległych, które serwer zamyka bez głosu) traktujemy jak zakończony, NIE „Oczekuje".
    // Decyzje automatyczne same kroku nie zamykają: eskalacja po terminie zostawia go otwartym
    // (stąd ton „current"), więc nie ma dla nich osobnego tonu.
    return 'approved';
  }
  return 'pending';
}

/** Zamrożony graf sprawy (`InstanceDetail.snapshot`) — to, czego potrzebujemy do nazwania drogi. */
export interface RouteSnapshot {
  nodes: readonly SnapshotNode[];
  edges: readonly SnapshotEdge[];
}

/**
 * Droga (krawędź) wybrana w decyzji — z zamrożonego grafu sprawy. `null`, gdy decyzja nie niesie
 * drogi (komentarz, automat, stary wpis), nie ma grafu albo krawędzi nie ma w grafie.
 */
export function findRouteEdge(
  snapshot: RouteSnapshot | null | undefined,
  routeEdgeId: string | null,
): SnapshotEdge | null {
  if (!routeEdgeId || !snapshot) return null;
  return snapshot.edges.find((e) => e.id === routeEdgeId) ?? null;
}

/**
 * Nazwa drogi: własna nazwa krawędzi, a gdy pusta — WŁASNA nazwa etapu docelowego (ta sama kolejność
 * zastępstw co `listAvailableRoutes` na serwerze, bez domyślnej nazwy typu etapu). `null`, gdy
 * żadnej nazwy nie ma — wołający podstawia wtedy własny napis.
 */
export function routeNameOf(edge: SnapshotEdge, nodes: readonly SnapshotNode[]): string | null {
  const own = edge.label?.trim();
  if (own) return own;
  return nodes.find((n) => n.id === edge.targetNodeId)?.label?.trim() || null;
}

/**
 * Nazwa drogi, którą zakończył się krok (ostatnia decyzja z drogą) — pokazywana na stepperze
 * zamiast ogólnego „Zakończony". `null`, gdy kroku nie zamknęła żadna droga rozpoznana w grafie
 * albo droga nie ma nazwy.
 */
export function stepRouteName(
  step: InstanceStep,
  snapshot: RouteSnapshot | null | undefined,
): string | null {
  for (let i = step.decisions.length - 1; i >= 0; i--) {
    const edge = findRouteEdge(snapshot, step.decisions[i]?.routeEdgeId ?? null);
    if (edge && snapshot) return routeNameOf(edge, snapshot.nodes);
  }
  return null;
}

/**
 * Kolor plakietki z NAZWĄ etapu końcowego zakończonej sprawy (np. „Zaakceptowano"). Nazwę nadaje
 * autor procesu, więc nie niesie znaczenia „sukces/porażka" — stąd kolor neutralny, nie
 * zielony/czerwony. Wspólny dla listy spraw i kolumny „Akceptacja" w Handlu.
 */
export const FINAL_NODE_CLASS = 'bg-bg-tertiary text-text-secondary';

/**
 * Nazwa etapu końcowego do pokazania zamiast statusu technicznego, albo `null`, gdy jej nie ma
 * (sprawa w toku / anulowana / starsza) lub jest pusta. Jedna reguła dla listy spraw, baneru,
 * historii i kolumny w Handlu: pusta nazwa to brak nazwy → pokazujemy status, nie pustą plakietkę.
 */
export function finalNodeLabelOrNull(label: string | null | undefined): string | null {
  const trimmed = label?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Klasa podświetlenia węzła, w którym jest sprawa (`data.isCurrent` w węzłach diagramu) —
 * BURSZTYNOWA, nie accent: etapy mają nagłówek accent (`DecisionNode`), a uchwyty połączeń też są
 * accent, więc niebieska ramka na niebieskim klocku ginęła (decyzja Alexandra 2026-10-13: „kolor
 * bursztynowy”). Bursztynu diagram nie używa nigdzie indziej (Start zielony, etapy niebieskie,
 * etapy końcowe neutralne). Odcień 600 w trybie jasnym — kontrast ramki z białym płótnem ≥ 3:1;
 * w ciemnym 400. Tę samą ramkę dostaje etap końcowy zakończonej sprawy (decyzja 2026-10-13 —
 * „bursztynowa jak bieżący”); oba stany różni plakietka (`CASE_MARKER_BADGE_CLASS`). Pasek etapów
 * (`TONE_DOT`) celowo zostaje w accent — tam nic się z nim nie zlewa. BEZ tła/tekstu: węzeł
 * buildera ma już własne kolory wg typu — dodanie bg/text nadpisałoby ten kolor. Ring jest czysto
 * addytywny (box-shadow), więc nie koliduje z żadną istniejącą klasą border/bg na węźle. Węzły
 * buildera nie ustawiają `isCurrent`, więc kreator procesu wygląda tak jak dotąd.
 */
export const CURRENT_NODE_RING_CLASS =
  'ring-4 ring-amber-600 ring-offset-2 dark:ring-amber-400 dark:ring-offset-gray-900';

/**
 * Znacznik położenia sprawy na diagramie: `current` = sprawa w toku, stoi na tym etapie;
 * `final` = sprawa zakończona, to jej etap końcowy.
 */
export type CaseMarker = 'current' | 'final';

/**
 * Klasy plakietki ("Tu jest sprawa" / "Sprawa zakończona") przy węźle diagramu. `current`
 * bursztynowa jak ramka węzła (`CURRENT_NODE_RING_CLASS`), z ciemnym tekstem — biały na bursztynie
 * byłby nieczytelny; `final` neutralnie ciemny — zakończona sprawa nie „czeka" na nikogo, więc
 * plakietka nie dostaje barwy akcji (ramka węzła jest ta sama dla obu stanów — to plakietka mówi,
 * czy sprawa stoi, czy się zakończyła).
 */
export const CASE_MARKER_BADGE_CLASS: Record<CaseMarker, string> = {
  current: 'bg-amber-400 text-gray-900',
  final: 'bg-gray-700 text-white dark:bg-gray-200 dark:text-gray-900',
};

/**
 * Znacznik dla węzła diagramu. Etap końcowy wygrywa nad „bieżącym" (sprawa zakończona nie ma
 * już bieżących etapów, więc kolizja oznaczałaby niespójne dane — wtedy pokazujemy to, co
 * serwer zapisał jako wynik sprawy). `null` = zwykły węzeł.
 */
export function caseMarkerOf(
  nodeId: string,
  currentNodeIds: ReadonlySet<string>,
  finalNodeId: string | null,
): CaseMarker | null {
  if (finalNodeId !== null && nodeId === finalNodeId) return 'final';
  if (currentNodeIds.has(nodeId)) return 'current';
  return null;
}
